import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import JsBarcode from "jsbarcode";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axiosInstance from "../services/axiosInterceptor";
import { getToken, getUserId } from "../services/authorize";

const LABEL_GRID_CSS = `
@media print {
  @page {
    margin: 8mm;
  }

  body {
    margin: 0 !important;
  }

  .library-barcode-page * {
    visibility: hidden !important;
  }

  .library-barcode-page .barcode-print-area,
  .library-barcode-page .barcode-print-area * {
    visibility: visible !important;
  }

  .library-barcode-page .barcode-print-area {
    position: absolute !important;
    top: 0;
    left: 0;
    width: 100%;
  }

  .main, .main-app {
    padding: 0 !important;
    margin: 0 !important;
  }

  .no-print {
    display: none !important;
  }

  .barcode-print-area {
    display: block !important;
  }

  .barcode-label-grid {
    grid-template-columns: repeat(3, 1fr) !important;
    gap: 10px !important;
    padding: 0 !important;
  }
}
`;

function BarcodeSvg({ value }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current || !value) return;
    try {
      JsBarcode(svgRef.current, value, {
        format: "CODE128",
        displayValue: false,
        margin: 0,
        width: 1.3,
        height: 38
      });
    } catch (error) {
      console.error("Barcode render error:", error);
    }
  }, [value]);

  return <svg ref={svgRef} style={{ width: "100%", maxWidth: 160, height: 40 }} />;
}

export default function LibraryBarcodeLabels() {
  const [masterData, setMasterData] = useState({ locations: [] });
  const [titles, setTitles] = useState([]);
  const [copies, setCopies] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingBatch, setSavingBatch] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [batchForm, setBatchForm] = useState({
    title_id: "",
    prefix: "BK",
    start_no: "1",
    quantity: "10",
    digit: "6",
    location_id: "",
    shelf_code: ""
  });

  const [existingFilter, setExistingFilter] = useState({
    keyword: "",
    title_id: ""
  });

  const [selectedCodes, setSelectedCodes] = useState([]);
  const [draftCodes, setDraftCodes] = useState([]);

  const headers = useMemo(() => ({ authorization: `Bearer ${getToken()}` }), []);

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      const [masterRes, titlesRes, copiesRes, shelvesRes] = await Promise.all([
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getMasterData`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getTitles`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getCopies`, { headers }),
        axiosInstance.get(`${process.env.REACT_APP_API}/library/getShelves`, { headers })
      ]);

      const nextMaster = masterRes.data || { locations: [] };
      const nextTitles = titlesRes.data || [];
      const nextCopies = copiesRes.data || [];
      const nextShelves = shelvesRes.data || [];

      setMasterData(nextMaster);
      setTitles(nextTitles);
      setCopies(nextCopies);
      setShelves(nextShelves);

      setBatchForm((prev) => ({
        ...prev,
        title_id: prev.title_id || String(nextTitles[0]?.title_id || "")
      }));
    } catch (fetchError) {
      setError("ไม่สามารถโหลดข้อมูลรหัสเล่มได้");
      console.error("LibraryBarcodeLabels fetch error:", fetchError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const selectedTitle = useMemo(() => {
    if (!batchForm.title_id) return null;
    return titles.find((title) => Number(title.title_id) === Number(batchForm.title_id)) || null;
  }, [titles, batchForm.title_id]);

  const titleOptions = useMemo(
    () =>
      titles.map((title) => ({
        value: String(title.title_id),
        label: `${title.title_code} - ${title.title_name}`
      })),
    [titles]
  );

  const titleFilterOptions = useMemo(
    () => [{ value: "", label: "ทุกชื่อหนังสือ" }, ...titleOptions],
    [titleOptions]
  );

  const selectedBatchTitleOption = useMemo(
    () => titleOptions.find((option) => option.value === String(batchForm.title_id)) || null,
    [titleOptions, batchForm.title_id]
  );

  const selectedFilterTitleOption = useMemo(
    () =>
      titleFilterOptions.find((option) => option.value === String(existingFilter.title_id)) ||
      titleFilterOptions[0],
    [titleFilterOptions, existingFilter.title_id]
  );

  const filteredShelfOptions = useMemo(() => {
    if (!batchForm.location_id) return [];

    return shelves
      .filter(
        (shelf) =>
          Number(shelf.tb_library_location_loc_id) === Number(batchForm.location_id) &&
          shelf.shelf_status === "active"
      )
      .map((shelf) => ({
        value: shelf.shelf_code,
        label: `${shelf.shelf_code}${shelf.shelf_name ? ` - ${shelf.shelf_name}` : ""}`
      }));
  }, [shelves, batchForm.location_id]);

  useEffect(() => {
    if (!batchForm.location_id && batchForm.shelf_code) {
      setBatchForm((prev) => ({ ...prev, shelf_code: "" }));
      return;
    }

    if (!batchForm.shelf_code) return;
    const stillValid = filteredShelfOptions.some((option) => option.value === batchForm.shelf_code);
    if (!stillValid) {
      setBatchForm((prev) => ({ ...prev, shelf_code: "" }));
    }
  }, [batchForm.location_id, batchForm.shelf_code, filteredShelfOptions]);

  const filteredExistingCopies = useMemo(() => {
    return copies.filter((copy) => {
      if (existingFilter.title_id && Number(copy.title_id) !== Number(existingFilter.title_id)) {
        return false;
      }
      if (!existingFilter.keyword.trim()) return true;
      const keyword = existingFilter.keyword.trim().toLowerCase();
      return (
        String(copy.copy_code || "").toLowerCase().includes(keyword) ||
        String(copy.title_name || "").toLowerCase().includes(keyword)
      );
    });
  }, [copies, existingFilter]);

  const previewBatchCodes = useMemo(() => {
    const prefix = batchForm.prefix.trim();
    const startNo = Number(batchForm.start_no || 0);
    const quantity = Number(batchForm.quantity || 0);
    const digit = Number(batchForm.digit || 6);

    if (!prefix || startNo <= 0 || quantity <= 0 || digit <= 0) return [];

    return Array.from({ length: quantity }).map((_, index) => {
      const running = startNo + index;
      return `${prefix}${String(running).padStart(digit, "0")}`;
    });
  }, [batchForm]);

  const duplicateInSystem = useMemo(() => {
    const inSystem = new Set(copies.map((copy) => String(copy.copy_code).toLowerCase()));
    return previewBatchCodes.filter((code) => inSystem.has(code.toLowerCase()));
  }, [copies, previewBatchCodes]);

  const selectedForPrint = useMemo(() => {
    const fromExisting = copies
      .filter((copy) => selectedCodes.includes(copy.copy_code))
      .map((copy) => ({
        copy_code: copy.copy_code,
        title_name: copy.title_name || "-",
        shelf_code: copy.shelf_code || "-"
      }));

    const fromDraft = draftCodes.map((code) => ({
      copy_code: code,
      title_name: selectedTitle?.title_name || "(รหัสใหม่)",
      shelf_code: batchForm.shelf_code || "-"
    }));

    return [...fromDraft, ...fromExisting];
  }, [copies, selectedCodes, draftCodes, selectedTitle, batchForm.shelf_code]);

  const toggleExistingCode = (copyCode) => {
    setSelectedCodes((prev) =>
      prev.includes(copyCode) ? prev.filter((code) => code !== copyCode) : [...prev, copyCode]
    );
  };

  const handleGenerateDraft = () => {
    setError("");
    setSuccess("");

    if (!batchForm.title_id) {
      setError("กรุณาเลือกชื่อหนังสือสำหรับรหัสใหม่");
      return;
    }

    if (previewBatchCodes.length === 0) {
      setError("ข้อมูลสร้างรหัสไม่ครบถ้วน");
      return;
    }

    if (duplicateInSystem.length > 0) {
      setError(`มีรหัสซ้ำในระบบ ${duplicateInSystem.length} รายการ กรุณาปรับช่วงเลข`);
      return;
    }

    setDraftCodes(previewBatchCodes);
    setSuccess(`สร้างรหัสใหม่แบบชั่วคราว ${previewBatchCodes.length} รายการแล้ว พร้อมพิมพ์หรือบันทึก`);
  };

  const handleSaveDraftToSystem = async () => {
    setError("");
    setSuccess("");

    const officerId = getUserId();
    if (!officerId) {
      setError("ไม่พบข้อมูลผู้ใช้งาน กรุณาเข้าสู่ระบบใหม่");
      return;
    }

    if (!batchForm.title_id || draftCodes.length === 0) {
      setError("ยังไม่มีรหัสใหม่สำหรับบันทึก");
      return;
    }

    try {
      setSavingBatch(true);

      for (const code of draftCodes) {
        await axiosInstance.post(
          `${process.env.REACT_APP_API}/library/addCopy`,
          {
            copy_code: code,
            title_id: Number(batchForm.title_id),
            location_id: batchForm.location_id ? Number(batchForm.location_id) : null,
            shelf_code: batchForm.shelf_code.trim() || null,
            t_id: officerId
          },
          { headers }
        );
      }

      setSuccess(`บันทึกรหัสเล่มใหม่เข้าระบบสำเร็จ ${draftCodes.length} รายการ`);
      setSelectedCodes((prev) => [...new Set([...draftCodes, ...prev])]);
      setDraftCodes([]);
      await fetchData();
    } catch (saveError) {
      const message = saveError?.response?.data?.message;
      if (message === "duplicate copy code") {
        setError("บันทึกรหัสไม่สำเร็จ: พบรหัสซ้ำในระบบ");
      } else {
        setError("บันทึกรหัสเล่มใหม่ไม่สำเร็จ");
      }
      console.error("Save draft barcode labels error:", saveError);
    } finally {
      setSavingBatch(false);
    }
  };

  const handlePrint = () => {
    if (selectedForPrint.length === 0) {
      setError("ยังไม่มีรายการสำหรับพิมพ์");
      return;
    }
    window.print();
  };

  return (
    <React.Fragment>
      <style>{LABEL_GRID_CSS}</style>
      <div className="library-barcode-page">
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">สร้างและพิมพ์ป้ายรหัสเล่ม (Barcode)</h4>
          <Badge bg="dark">Labels</Badge>
        </div>

        {error && (
          <Alert variant="danger" className="mb-3 no-print">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3 no-print">
            {success}
          </Alert>
        )}

        <Row className="g-3 mb-3 no-print">
          <Col xl={6}>
            <Card>
              <Card.Header className="fw-semibold">สร้างรหัสเล่มใหม่แบบชุด</Card.Header>
              <Card.Body>
                <Row className="g-2">
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>ชื่อหนังสือ</Form.Label>
                      <Select
                        options={titleOptions}
                        value={selectedBatchTitleOption}
                        onChange={(option) => setBatchForm((prev) => ({ ...prev, title_id: option?.value || "" }))}
                        isSearchable
                        isClearable
                        placeholder="เลือกชื่อหนังสือ"
                        noOptionsMessage={() => "ไม่พบข้อมูล"}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>จำนวน (ดวง)</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        max={200}
                        value={batchForm.quantity}
                        onChange={(event) => setBatchForm((prev) => ({ ...prev, quantity: event.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-2">
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>คำนำหน้า</Form.Label>
                      <Form.Control
                        value={batchForm.prefix}
                        onChange={(event) => setBatchForm((prev) => ({ ...prev, prefix: event.target.value.toUpperCase() }))}
                        placeholder="BK"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>เลขเริ่มต้น</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={batchForm.start_no}
                        onChange={(event) => setBatchForm((prev) => ({ ...prev, start_no: event.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>จำนวนหลักตัวเลข</Form.Label>
                      <Form.Control
                        type="number"
                        min={3}
                        max={10}
                        value={batchForm.digit}
                        onChange={(event) => setBatchForm((prev) => ({ ...prev, digit: event.target.value }))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ตำแหน่งจัดเก็บ (ถ้าบันทึกเข้าระบบ)</Form.Label>
                      <Form.Select
                        value={batchForm.location_id}
                        onChange={(event) =>
                          setBatchForm((prev) => ({
                            ...prev,
                            location_id: event.target.value,
                            shelf_code: ""
                          }))
                        }
                      >
                        <option value="">ไม่ระบุ</option>
                        {(masterData.locations || []).map((loc) => (
                          <option key={loc.loc_id} value={loc.loc_id}>
                            {loc.loc_code || "-"} - {loc.loc_name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>รหัสชั้นวาง</Form.Label>
                      <Form.Select
                        value={batchForm.shelf_code}
                        onChange={(event) => setBatchForm((prev) => ({ ...prev, shelf_code: event.target.value }))}
                        disabled={!batchForm.location_id}
                      >
                        <option value="">
                          {!batchForm.location_id ? "เลือกตำแหน่งจัดเก็บก่อน" : "เลือกรหัสชั้นวาง"}
                        </option>
                        {filteredShelfOptions.map((shelfOption) => (
                          <option key={shelfOption.value} value={shelfOption.value}>
                            {shelfOption.label}
                          </option>
                        ))}
                      </Form.Select>
                      {batchForm.location_id && filteredShelfOptions.length === 0 && (
                        <Form.Text className="text-muted">
                          ยังไม่มีรหัสชั้นวางในตำแหน่งนี้ กรุณาเพิ่มในหน้าจัดการข้อมูลแม่บท
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex gap-2 flex-wrap">
                  <Button variant="primary" onClick={handleGenerateDraft}>
                    สร้างชุดรหัสใหม่
                  </Button>
                  <Button variant="success" onClick={handleSaveDraftToSystem} disabled={savingBatch || draftCodes.length === 0}>
                    {savingBatch ? "กำลังบันทึก..." : "บันทึกรหัสใหม่เข้าระบบ"}
                  </Button>
                </div>

                <div className="small text-muted mt-3">
                  {selectedTitle ? `ชื่อหนังสือที่เลือก: ${selectedTitle.title_name}` : "ยังไม่ได้เลือกชื่อหนังสือ"}
                </div>
                {duplicateInSystem.length > 0 && (
                  <Alert variant="warning" className="mt-2 mb-0">
                    พบรหัสซ้ำ: {duplicateInSystem.slice(0, 5).join(", ")}{duplicateInSystem.length > 5 ? " ..." : ""}
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={6}>
            <Card>
              <Card.Header className="fw-semibold">เลือกรหัสเดิมที่มีในระบบเพื่อพิมพ์ซ้ำ</Card.Header>
              <Card.Body>
                <Row className="g-2 mb-2">
                  <Col md={6}>
                    <Form.Control
                      placeholder="ค้นหารหัสเล่ม / ชื่อหนังสือ"
                      value={existingFilter.keyword}
                      onChange={(event) => setExistingFilter((prev) => ({ ...prev, keyword: event.target.value }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Select
                      options={titleFilterOptions}
                      value={selectedFilterTitleOption}
                      onChange={(option) => setExistingFilter((prev) => ({ ...prev, title_id: option?.value || "" }))}
                      isSearchable
                      placeholder="ค้นหาชื่อหนังสือ"
                      noOptionsMessage={() => "ไม่พบข้อมูล"}
                    />
                  </Col>
                </Row>

                <div className="border rounded" style={{ maxHeight: 280, overflowY: "auto" }}>
                  <Table hover responsive className="mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: 50 }}></th>
                        <th>รหัสเล่ม</th>
                        <th>ชื่อหนังสือ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && filteredExistingCopies.length === 0 && (
                        <tr>
                          <td colSpan={3} className="text-center text-muted py-3">ไม่พบข้อมูล</td>
                        </tr>
                      )}
                      {filteredExistingCopies.slice(0, 200).map((copy) => (
                        <tr key={copy.copy_id}>
                          <td>
                            <Form.Check
                              checked={selectedCodes.includes(copy.copy_code)}
                              onChange={() => toggleExistingCode(copy.copy_code)}
                            />
                          </td>
                          <td>{copy.copy_code}</td>
                          <td>{copy.title_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="small text-muted mt-2">
                  เลือกแล้วจากรหัสเดิม: {selectedCodes.length} รายการ
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-3 no-print">
          <Card.Body className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <div className="fw-semibold">รายการพร้อมพิมพ์</div>
              <div className="small text-muted">
                รหัสใหม่ (ยังไม่บันทึก): {draftCodes.length} | รหัสเดิมที่เลือก: {selectedCodes.length} | รวม: {selectedForPrint.length}
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={() => setDraftCodes([])} disabled={draftCodes.length === 0}>
                ล้างรหัสใหม่
              </Button>
              <Button variant="dark" onClick={handlePrint} disabled={selectedForPrint.length === 0}>
                พิมพ์ป้ายบาร์โค้ด
              </Button>
            </div>
          </Card.Body>
        </Card>

        <div className="barcode-print-area">
          {selectedForPrint.length > 0 ? (
            <div
              className="barcode-label-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: "12px"
              }}
            >
              {selectedForPrint.map((item, index) => (
                <Card key={`${item.copy_code}-${index}`} style={{ border: "1px solid #adb5bd" }}>
                  <Card.Body style={{ padding: "10px" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2, minHeight: 30 }}>
                      {item.title_name}
                    </div>
                    <div className="d-flex justify-content-center my-1">
                      <BarcodeSvg value={item.copy_code} />
                    </div>
                    <div style={{ textAlign: "center", fontSize: 13, fontWeight: 700, letterSpacing: 1 }}>
                      {item.copy_code}
                    </div>
                    <div style={{ textAlign: "center", fontSize: 11, color: "#6c757d" }}>
                      {item.shelf_code || "-"}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <Card.Body className="text-center text-muted py-4">
                ยังไม่มีรายการป้ายสำหรับพิมพ์
              </Card.Body>
            </Card>
          )}
        </div>

        {loading && (
          <div className="text-center py-3 no-print">
            <Spinner animation="border" size="sm" className="me-2" /> กำลังโหลดข้อมูล...
          </div>
        )}
      </main>
      <Footer />
      </div>
    </React.Fragment>
  );
}
